import React, { useState } from "react";
import { CoachRecording, ManualReviewStatus } from "../types/coach-recording.entity";
import { formatScreamingSnakeCase, languageTuples, mergeFilterCleanly } from "src/utils/util";

type CoachRecordingFilterProps = {
  updateFilterQuery: (where: Partial<CoachRecording>) => void;
};

const statusOptions: (CoachRecording["manualReviewStatus"] | "ALL")[] = [
  "ALL",
  "PENDING",
  "APPROVED",
  "REJECTED",
  "UNDER_REVIEW",
  "USER_CANCELED",
];

export const CoachRecordingFilter: React.FC<CoachRecordingFilterProps> = ({ updateFilterQuery }) => {
  const [queryFilter, setQueryFilter] = useState<Partial<CoachRecording>>({});

  const handleUpdateFilterQuery = (filter: Partial<CoachRecording>) => {
    const updated = mergeFilterCleanly(queryFilter, filter);
    setQueryFilter(() => updated);
    updateFilterQuery(updated);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      case 'UNDER_REVIEW':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
          </svg>
        );
      case 'APPROVED':
        return (
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'REJECTED':
        return (
          <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'USER_CANCELED':
        return (
          <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '⏳';
      case 'UNDER_REVIEW':
        return '👁️';
      case 'APPROVED':
        return '✅';
      case 'REJECTED':
        return '❌';
      case 'USER_CANCELED':
        return '🚫';
      default:
        return '📋';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100';
      case 'UNDER_REVIEW':
        return 'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100';
      case 'APPROVED':
        return 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100';
      case 'REJECTED':
        return 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100';
      case 'USER_CANCELED':
        return 'border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100';
    }
  };

  return (
    <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-2xl shadow-lg p-4 border border-gray-200/60 mb-6">

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Language Filter */}
        <div className="group">
          <label htmlFor="coachRecodingLangSelect" className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9v3a1 1 0 01-2 0V6H4a1 1 0 110-2h3V3a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </div>
            <span>Language</span>
          </label>
          <div className="relative">
            <select
              id="coachRecodingLangSelect"
              onChange={(e) => handleUpdateFilterQuery({ langCode: e.target.value })}
              className="w-full px-3 py-2 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 hover:border-gray-300 group-hover:shadow-md"
            >
              <option value="">🌍 All Languages</option>
              {languageTuples.map(([code, value]) => (
                <option key={code} value={code}>
                  {value}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Status Filter */}
        <div className="group">
          <label htmlFor="coachRecodingStatusSelect" className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
            <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
            </div>
            <span>Status</span>
          </label>
          <div className="relative">
            <select
              id="coachRecodingStatusSelect"
              onChange={(e) =>
                handleUpdateFilterQuery({
                  manualReviewStatus: e.target.value as ManualReviewStatus,
                })
              }
              className="w-full px-3 py-2 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 hover:border-gray-300 group-hover:shadow-md"
            >
                           {statusOptions.map((opt) => (
               <option key={opt} value={opt === "ALL" ? "" : opt}>
                 {opt === "ALL" ? "📋 All Statuses" : `${getStatusEmoji(opt)} ${formatScreamingSnakeCase(opt)}`}
               </option>
             ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {Object.keys(queryFilter).length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 mb-3">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Active Filters:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(queryFilter).map(([key, value]) => {
              // Only render if value is a string
              if (typeof value !== 'string') return null;
              
              return (
                <span
                  key={key}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(value)} border transition-all duration-300 hover:scale-105`}
                >
                  {key === 'langCode' ? '🌍' : getStatusIcon(value)}
                  <span className="ml-1">
                    {key === 'langCode' 
                      ? languageTuples.find(([code]) => code === value)?.[1] || value
                      : formatScreamingSnakeCase(value)
                    }
                  </span>
                  <button
                    onClick={() => handleUpdateFilterQuery({ [key]: undefined })}
                    className="ml-2 hover:bg-white/50 rounded-full p-0.5 transition-colors duration-200"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              );
            })}
            <button
              onClick={() => {
                setQueryFilter({});
                updateFilterQuery({});
              }}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 transition-all duration-300 hover:scale-105"
            >
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Custom CSS for enhanced animations */}
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-slide-in-up {
          animation: slideInUp 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};
